<template>
  <div
    :id="idForLink"
    class="comment-container"
    :class="`comment-level-${isPinned ? '0' : comment.level}`"
  >
    <PDPCommentStatusTag
      v-if="userIsModerator"
      :approved="approvedStatus"
      :resolved="resolvedStatus"
    />
    <!--
      rendering for discussion page differs enough from the standard layout that I am just going to
      render it as its own block.
    -->
    <div
      v-if="isDiscussion && forDiscussionPage"
      class="discussion-main-body"
    >
      <h1
        v-if="truncatedCommentTitle"
        class="pdp-page-title"
      >
        {{ truncatedCommentTitle }}
      </h1>
      <p
        v-if="titleIsTruncated"
        class="pdp-text"
      >
        {{ commentTitle }}
      </p>
      <PDPCommentRatings
        v-if="forReviews && isDiscussion && !isPinned"
        :rating="comment.rating"
      />
      <PDPCommentBody
        v-if="comment.title"
        :comment="comment.comment"
        :show-mod-message="showModMessage"
      />
      <!-- for discussion pages let's ensure that the author is a single row with avatar on left -->
      <div class="d-flex">
        <div class="comment-avatar mr-2 mr-sm-4">
          <img
            class="rounded-circle"
            loading="lazy"
            width="70"
            height="70"
            alt="user-avatar"
            :src="avatarSrc"
          >
        </div>
        <PDPUserProfile
          :comment="comment"
          :commenter-is-deal-partner="commenterIsDealPartner"
        />
      </div>
      <PDPCommentButtons
        v-if="!isPinned"
        :id="comment.id"
        :is-authenticated="user.isAuthenticated"
        :is-email-verified="!!user.isEmailVerified"
        :no-reply="noReply"
        :upvote-count="upvoteCount"
        :helpful-btn-starting-state="helpfulBtnStartingState"
        @reply-comment="replyComment"
        @update-action="updateAction"
      />
      <PDPCommentControls
        :show-link="!isDiscussion"
        :fragment-for-link="fragmentForLink"
        :discussion-display-path="discussionDisplayPath"
        :comment-type-title="commentTypeTitle"
        :approved="approvedStatus"
        :resolved="resolvedStatus"
        :pinned="pinnedStatus"
        :blacklist="blacklistStatus"
        :whitelist="whitelistStatus"
        :filter-comments="filterComments"
        :is-pinned="isPinned"
        :user="user"
        :for-reviews="forReviews"
        @update-action="updateAction"
      />
    </div>
    <!-- this is for the non discussion page UI -->
    <div
      v-else
      class="comment-main-body d-flex p-3 p-md-5"
      :class="[pinnedClass, statusOutlineClass]"
    >
      <div class="comment-avatar mr-2 mr-sm-4">
        <img
          class="rounded-circle"
          loading="lazy"
          width="70"
          height="70"
          alt="user-avatar"
          :src="avatarSrc"
        >
      </div>
      <div class="comment-content">
        <PDPUserProfile
          :comment="comment"
          :commenter-is-deal-partner="commenterIsDealPartner"
        />
        <PDPCommentRatings
          v-if="forReviews && isDiscussion && !isPinned"
          :rating="comment.rating"
        />
        <PDPCommentBody
          :comment="comment.comment"
          :show-mod-message="showModMessage"
          :title="comment.title"
        />
        <PDPCommentButtons
          v-if="!isPinned"
          :id="comment.id"
          :is-authenticated="user.isAuthenticated"
          :is-email-verified="!!user.isEmailVerified"
          :no-reply="noReply"
          :upvote-count="upvoteCount"
          :helpful-btn-starting-state="helpfulBtnStartingState"
          @reply-comment="replyComment"
          @update-action="updateAction"
        />
        <PDPCommentControls
          :fragment-for-link="fragmentForLink"
          :discussion-display-path="discussionDisplayPath"
          :comment-type-title="commentTypeTitle"
          :approved="approvedStatus"
          :resolved="resolvedStatus"
          :pinned="pinnedStatus"
          :blacklist="blacklistStatus"
          :whitelist="whitelistStatus"
          :filter-comments="filterComments"
          :is-pinned="isPinned"
          :user="user"
          :for-reviews="forReviews"
          @update-action="updateAction"
        />
      </div>
    </div>
    <div
      v-if="!isPinned && comment.children && comment.children.length"
      :class="{ 'comment-children-outer-container': isDiscussion }"
    >
      <!-- never include show/hide replies on the discussion page -->
      <div
        v-if="isDiscussion && !forDiscussionPage"
        class="comment-show-hide-container"
      >
        <div class="text-right">
          <span
            class="comment-show-hide"
            :class="{ 'collapsed': !showChildren }"
            @click="onClickShowHide"
          >
            {{ showChildren ? 'Hide' : 'Show' }} replies
            <span
              v-if="commentCount"
              class="comment-count"
            >
              {{ commentCount }}
            </span>
          </span>
        </div>
        <div
          ref="sliderParent"
          class="comment-slider-parent"
        >
          <div
            ref="sliderChild"
            class="comment-children-inner-container"
          >
            <div
              v-for="(child, index) in comment.children"
              :key="`${child.id}-${index}-${child.comment}`"
              class="comment-child"
              :class="{ 'comment-last-child': !(child.comments && child.comments.length) }"
            >
              <PDPCommentNode
                :comment-type-title="commentTypeTitle"
                :filter-comments="filterComments"
                :discussion="discussion"
                :comment="child"
                :deal-partner-user-ids="dealPartnerUserIds"
                :position-indices="updateIndex(index)"
                :user="user"
                :for-reviews="forReviews"
                @update-action="updateChildAction"
                @reply-comment="replyChildComment"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="comment-children">
          <div
            v-for="(child, index) in comment.children"
            :key="`${child.id}-${index}-${child.comment}`"
            class="comment-child"
            :class="{ 'comment-last-child': !(child.comments && child.comments.length) }"
          >
            <PDPCommentNode
              no-reply
              :comment-type-title="commentTypeTitle"
              :filter-comments="filterComments"
              :discussion="discussion"
              :comment="child"
              :deal-partner-user-ids="dealPartnerUserIds"
              :position-indices="updateIndex(index)"
              :user="user"
              :for-reviews="forReviews"
              @update-action="updateChildAction"
              @reply-comment="replyChildComment"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';
import { firstSentence } from '@/util/global';
import PDPCommentBody from './PDPCommentBody.vue';
import PDPCommentButtons from './PDPCommentButtons.vue';
import PDPCommentControls from './PDPCommentControls.vue';
import PDPCommentRatings from './PDPCommentRatings.vue';
import PDPUserProfile from './PDPUserProfile.vue';
import DefaultAvatar from '../../../../static/images/profile/as-profile-default-avatar.svg';

export default {
  name: 'PDPCommentNode',
  components: {
    PDPCommentBody,
    PDPCommentButtons,
    PDPCommentControls,
    PDPCommentRatings,
    PDPUserProfile,
    PDPCommentStatusTag: () => import('./PDPCommentStatusTag.vue'),
  },
  inject: {
    deal: {},
    forDiscussionPage: {
      default: false,
    },
  },
  props: {
    filterComments: {
      type: Boolean,
      default: false,
    },
    // jw: this is the discussion that the comment is part of.
    discussion: {
      type: Object,
      required: true,
    },
    // jw: this is the actual comment itself, and for root level comments it will be the same as
    //     the discussion.
    comment: {
      type: Object,
      required: true,
    },
    commentTypeTitle: {
      type: String,
      required: true,
    },
    dealPartnerUserIds: {
      type: Array,
      required: true,
    },
    isPinned: {
      type: Boolean,
    },
    positionIndices: {
      type: Array,
      default: () => ([]),
    },
    forceShowAll: {
      type: Boolean,
    },
    // jw: this property is only relevant to parent comments and is always provided in that case.
    //     Replies do not support expand/collapse so I am defaulting this to null since neither
    //     state is relevant.
    showAllChildren: {
      type: Boolean,
      default: null,
    },
    user: {
      type: Object,
      default: () => ({}),
    },
    forReviews: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      approvedStatus: this.comment.approved,
      blacklistStatus: this.comment.user.comment_blacklist,
      pinnedStatus: this.comment.pinned,
      resolvedStatus: this.comment.resolved,
      upvoteCount: this.comment.num_likes,
      whitelistStatus: this.comment.user.comment_whitelist,
      // jw: default show children to the showAllChildren flag, otherwise we will render in conflict
      //     with the visible toggle.
      showChildren: this.showAllChildren,
    };
  },
  computed: {
    userIsModerator() {
      return this.$store.getters['discussion/userIsModerator'];
    },
    noReply() {
      return this.comment.level === 2;
    },
    helpfulBtnStartingState() {
      return !!this.comment.likedit_user_ids?.includes(this.user.id);
    },
    truncatedCommentTitle() {
      const title = firstSentence(this.comment.title || this.comment.comment || '');
      if (this.forDiscussionPage && this.forReviews) {
        return `${this.deal.public_name} Review: ${title}`;
      }
      return title;
    },
    titleIsTruncated() {
      const title = firstSentence(this.commentTitle);

      return title !== this.commentTitle;
    },
    commentTitle() {
      return this.comment.title || this.comment.comment;
    },
    prefixForId() {
      if (this.forReviews) {
        return 'r';
      }
      return 'q';
    },
    supportsDiscussionPages() {
      return !!this.deal.render_nuxt_pdp.support_discussion_pages;
    },
    isDiscussion() {
      return this.discussion.id === this.comment.id; // root element or level 0
    },
    idForLink() {
      // jw: do not include the prefix for id when we are linking to the discussion page.
      if (this.supportsDiscussionPages) {
        return this.comment.id;
      }
      return `${this.prefixForId}${this.comment.id}`;
    },
    fragmentForLink() {
      // jw: always include the fragment in the link if discussion pages are not supported, or if
      //     this is a reply.
      if (!this.supportsDiscussionPages || !this.isDiscussion) {
        return this.idForLink;
      }
      return null;
    },
    discussionDisplayPath() {
      if (this.supportsDiscussionPages) {
        return this.discussion.display_path;
      }

      return this.deal.get_absolute_url;
    },
    avatarSrc() {
      const avatar = get(this, ['comment', 'user', 'avatar']);

      // jl: using optional chaining here breaks test case (for the time being)
      if (!avatar || !avatar.includes('/media/')) {
        // jl: user has not uploaded their own profile pic, override default gravatar
        return `${DefaultAvatar}?width=100&height=100&aspect_ratio=1:1`;
      }

      const queryParams = {};
      const [url, params = ''] = avatar.split('?');

      // Retain all existing params
      if (params.length) {
        const pairs = params.split('&');

        pairs.forEach((pair) => {
          const [key, value] = pair.split('=');
          queryParams[decodeURIComponent(key)] = decodeURIComponent(value || '');
        });
      }

      // Override (or add to) any existing params
      queryParams.width = 100;
      queryParams.height = 100;
      queryParams.aspect_ratio = '1:1';

      const keys = Object.keys(queryParams);
      let newUrl = `${url}?`;

      keys.forEach((key, index) => {
        newUrl += `${index !== 0 ? '&' : ''}${key}=${queryParams[key]}`;
      });

      return newUrl;
    },
    commentCount() {
      return this.isDiscussion ? this.calcCount(this.comment) - 1 : 0;
    },
    pinnedClass() {
      // todo:LED-1136: We need to support this for questions eventually.
      return (this.pinnedStatus && this.forReviews) ? 'comment-status-pinned' : '';
    },
    commenterIsDealPartner() {
      return this.dealPartnerUserIds.includes(this.comment.user.id);
    },
    statusOutlineClass() {
      if (this.userIsModerator) {
        if (typeof this.approvedStatus === 'undefined' || this.approvedStatus === null) {
          return 'comment-status-unmoderated';
        }

        if (!this.approvedStatus) {
          return 'comment-status-moderated';
        }

        if (!this.resolvedStatus) {
          return 'comment-status-unresolved';
        }
      }

      if (!this.isDiscussion) {
        if (this.commenterIsDealPartner) {
          return 'comment-status-creator';
        }
        return 'comment-status-active';
      }

      return '';
    },
    showModMessage() {
      return (
        typeof this.approvedStatus === 'undefined'
          || this.approvedStatus === null
          || !this.approvedStatus
      )
        && this.user.id === this.comment.user.id;
    },
    childrenLength() {
      return (this.comment.children && this.comment.children.length) || 0;
    },
  },
  watch: {
    showAllChildren(val) {
      this.onClickShowHide(val);
    },
    forceShowAll(val) {
      this.onClickShowHide(val);
    },
    comment() {
      // After comment data refresh, reset statuses
      this.approvedStatus = this.comment.approved;
      this.blacklistStatus = this.comment.user.comment_blacklist;
      this.pinnedStatus = this.comment.pinned;
      this.resolvedStatus = this.comment.resolved;
      this.whitelistStatus = this.comment.user.comment_whitelist;
    },
    childrenLength(newLength, oldLength) {
      // jw: if the number of replies within this node grows we want to show the children. This
      //     should only happen when a reply is made by the current user.
      if (this.isDiscussion && newLength > oldLength) {
        // jw: if this is the first reply we need to force a render so that the height will be setup
        //     since it never was before.
        this.onClickShowHide(true, newLength === 1);
      }
    },
  },
  mounted() {
    const { sliderParent, sliderChild } = this.$refs;

    if (!this.forceShowAll || !sliderParent || !sliderChild) {
      return;
    }

    this.onClickShowHide(this.showChildren, true);
  },
  methods: {
    /* eslint-disable no-param-reassign */
    setSliderHeightAndFollow(sliderElement, newHeight) {
      sliderElement.style.height = `${newHeight}px`;

      // Hack to make sure any future height changes are adjusted (animation is 250ms)
      setTimeout(() => {
        sliderElement.style.height = 'auto';
      }, 300);
    },
    /* eslint-enable no-param-reassign */
    calcCount(comment) {
      let count = 1;
      if (comment && comment.children) {
        for (let i = 0; i < comment.children.length; i += 1) {
          count += this.calcCount(comment.children[i]);
        }
      }

      return count;
    },
    updateState(data) {
      const { type, value, error } = data;

      if (error) {
        this.$toastr.e('Something went wrong');
        return;
      }

      switch (type) {
        case 'approved':
          this.approvedStatus = value;
          break;
        case 'resolved':
          this.resolvedStatus = value;
          break;
        case 'upvote':
          this.upvoteCount = value;
          return; // jl: let's not display 'success' toast for upvote, as UI is self-explanatory
        case 'whitelist':
          this.whitelistStatus = value;
          break;
        case 'blacklist':
          this.blacklistStatus = value;
          break;
        case 'pinned':
          this.pinnedStatus = value;
          break;
        default:
          break;
      }

      this.$toastr.s('Success');
    },
    replyComment() {
      const {
        id, title, comment, level, user: { username },
      } = this.comment;

      this.$emit('reply-comment', {
        comment: {
          id, title, comment, level, username,
        },
        positionIndices: this.positionIndices,
      });
    },
    replyChildComment(data) {
      this.$emit('reply-comment', data);
    },
    updateAction(data) {
      this.$emit('update-action', {
        ...data,
        id: this.comment.id,
        commentUserId: get(this, ['comment', 'user', 'id']),
        done: this.updateState,
      });
    },
    updateChildAction(data) {
      this.$emit('update-action', data);
    },
    updateIndex(index) {
      const updatedIndices = [...this.positionIndices];
      updatedIndices.push(index);

      return updatedIndices;
    },
    onClickShowHide(val, forceRender = false) {
      // jw: if the provided value matches we can short out. Note: we are doing explicit matches
      //     meaning that the link which provides an event object will not match here and will fall
      //     through.
      if (val === this.showChildren && !forceRender) {
        return;
      }

      this.$nextTick(() => {
        const { sliderParent, sliderChild } = this.$refs;

        // jw: if val is not a boolean then it will be a click event object for the link from the
        //     page, in which case we want to treat this like a toggle.
        if (typeof val === 'boolean') {
          this.showChildren = val;
        } else {
          this.showChildren = !this.showChildren;
        }

        // jw: it is vital that we short out prior to performing animations, but after we establish
        //     the appropriate value above. This way when replies are made we will be testing
        //     against the intended state of the component.
        if (!sliderParent || !sliderChild) {
          return;
        }

        if (!this.showChildren) {
          sliderParent.style.height = `${sliderChild.clientHeight}px`;

          setTimeout(() => {
            sliderParent.style.height = '0';
          }, 50);
        } else {
          this.setSliderHeightAndFollow(sliderParent, sliderChild.clientHeight);
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~/styles/utils/breakpoints';

  .comment-content {
    width: calc(100% - 1.5rem - 70px); /* 1.5rem for padding, 70px for avatar */
  }

  .comment-main-body {
    border: 1px solid transparent;
    box-sizing: border-box;
    border-radius: 0 3px 3px;
    background-clip: padding-box; /* for Safari */
  }

  .comment-level-0 {
    padding-left: 0;
    padding-right: 0;
  }

  .comment-level-1 {
    padding-left: 3rem;
    padding-right: 1.5rem;
  }

  .comment-level-2 {
    padding-left: 3rem;
    padding-right: 1.5rem;
    margin-right: -1.5rem;
  }

  .comment-level-1:not(:last-child),
  .comment-level-2:not(:last-child) {
    margin-bottom: 1.5rem;
  }

  .comment-level-3 {
    padding-left: 6rem;
  }

  .comment-avatar img {
    width: 70px;
    height: 70px;
    object-fit: cover;
  }

  .comment-status-active {
    background: #fff;
    border-radius: 5px;
    border: 1px solid rgba(94, 189, 192, 0.65);
  }

  .comment-status-unmoderated {
    border-color: #ff7a30;
    box-shadow: 0 2px 20px rgba(255, 122, 48, 0.5) !important;
  }

  .comment-status-unresolved {
    border-color: #8458ff;
    box-shadow: 0 2px 20px rgba(132, 88, 255, 0.5) !important;
  }

  .comment-status-moderated {
    border-color: #eb5757;
    box-shadow: 0 2px 20px rgba(235, 87, 87, 0.5) !important;
  }

  .comment-status-creator {
    background: #fff;
    border-color: #00b3bb;
    border-radius: 5px;
  }

  .comment-status-pinned {
    box-shadow: inset 0 0 10px 0 #00b3bb !important;
  }

  .comment-child {
    padding-top: 3rem;
    padding-bottom: 3rem;
  }

  .comment-child .comment-children {
    padding-left: 3rem;
  }

  .comment-child.comment-last-child {
    padding-bottom: 1.5rem;
  }

  .comment-children-outer-container {
    border-top: 1px solid #ddd;
    background: #fafafa;
    border-bottom-left-radius: 15px;
    border-bottom-right-radius: 15px;
  }

  .comment-slider-parent {
    transition: height 250ms ease-in-out;
    height: 0;
    overflow: hidden;
  }

  .comment-show-hide-container {
    font-size: 0.8rem;
    position: relative;
    background: #fafafa;
    padding: 1rem 3rem;
    color: #555;
    border-bottom-left-radius: 15px;
    border-bottom-right-radius: 15px;
  }

  .comment-show-hide-container .comment-count {
    height: 1.2rem;
    width: 1.2rem;
    font-size: 0.7rem;
    border-radius: 50%;
    border: 1px solid #555;
    display: inline-block;
    text-align: center;
    margin-left: 0.3rem;
    top: -1px;
    position: relative;
    transition: all 0.3s;
  }

  .comment-show-hide-container .comment-show-hide {
    position: relative;
    padding-left: 1.5em;
    cursor: pointer;
    opacity: 0.85;
    transition: all 0.3s;
  }

  .comment-show-hide-container .comment-show-hide:hover {
    position: relative;
    padding-left: 1.5em;
    cursor: pointer;
    opacity: 1;
    transition: all 0.3s;
  }

  .comment-show-hide-container .comment-show-hide:hover .comment-count {
    color: #ffbc00;
    border-color: #ffbc00;
  }

  .comment-show-hide-container .comment-show-hide::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
    left: 0.2em;
    top: 25%;
    border-width: 0 0.35em 0.5em;
    border-color: transparent transparent #777;
    transition: transform 0.3s;
  }

  .comment-show-hide-container .comment-show-hide.collapsed::before {
    transform: rotate(180deg);
  }

  @include media-breakpoint-up(md) {
    .pdp-page-title {
      -webkit-line-clamp: 2;
    }
  }

  @include media-breakpoint-down(md) {
    .comment-show-hide-container {
      padding: 1rem;
    }

    .comment-child .comment-children {
      padding-left: 1rem;
    }

    .comment-show-hide {
      padding-right: 1rem;
    }

    .comment-avatar img {
      width: 2.5rem;
      height: 2.5rem;
    }

    .comment-level-1 {
      padding-left: 1.3rem;
    }

    .comment-level-2,
    .comment-level-3 {
      padding-left: 1.5rem;
    }
  }
</style>
